import React, { useCallback, useEffect, useState } from 'react';
import Joi from 'joi';

import { useUpdateInvestorProfileMutation } from 'api/main';
import { useMountedState } from 'utils/hooks';
import { validateSchema } from 'utils/helpers';
import { EMAIL_VALIDATION_OPTIONS } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { CustomTextField, Loader } from 'components';
import { Box, FormHelperText, IconButton, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as icons from 'mdi-material-ui';

export const emailSchema = Joi.string().required().email(EMAIL_VALIDATION_OPTIONS).messages({
    'string.empty': texts.ADRESSE_EMAIL_SAISIE_PAS_VALIDE,
    'string.email': texts.ADRESSE_EMAIL_SAISIE_PAS_VALIDE,
});

const useStyles = makeStyles(() => ({
    field: { marginBottom: 0, flex: '0 1 75%' },
}));

function EditableEmailAddress({
    defaultValue,
    onDelete,
    onUpdate,
    deleteDisabled,
    avoidCallbacks,
}) {
    const mounted = useMountedState();
    const classes = useStyles();
    const [value, setValue] = useState(defaultValue);
    const [editing, setEditing] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const [, { isSuccess }] = useUpdateInvestorProfileMutation({
        fixedCacheKey: 'investor-emails',
    });

    const handleChange = useCallback(
        e => {
            setValue(e.target.value);
            if (avoidCallbacks) {
                onUpdate(e.target.value);
            }
            setError('');
        },
        [avoidCallbacks, onUpdate]
    );
    const handleEdit = useCallback(() => {
        setEditing(true);
    }, []);
    const handleCancel = useCallback(() => {
        setEditing(false);
    }, []);

    const handleDelete = useCallback(() => {
        if (!avoidCallbacks) {
            setLoading(true);
        }
        onDelete();
    }, [onDelete, avoidCallbacks]);
    const handleSave = useCallback(() => {
        const { valid, newErrors } = validateSchema(emailSchema, value);
        if (valid) {
            if (!avoidCallbacks) {
                setLoading(true);
            }
            onUpdate(value);
        } else {
            setError(newErrors['']);
        }
    }, [value, onUpdate, avoidCallbacks]);

    const handleKeyUp = useCallback(
        e => {
            if (e.key === 'Enter') {
                handleSave();
            }
        },
        [handleSave]
    );

    useEffect(() => {
        if (isSuccess && mounted) {
            setLoading(false);
            setEditing(false);
        }
    }, [isSuccess, mounted]);

    return (
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            {editing ? (
                <CustomTextField
                    type="email"
                    inputStyle={classes.field}
                    placeholder={texts.E_MAIL}
                    value={value}
                    onChange={handleChange}
                    onKeyUp={handleKeyUp}
                    error={error}
                />
            ) : (
                <Typography>{value}</Typography>
            )}
            <Box>
                <Loader loading={loading} marginTop={0} size={20}>
                    {!avoidCallbacks && (
                        <IconButton
                            size="small"
                            color={editing ? 'primary' : 'grey'}
                            disableRipple
                            onClick={editing ? handleSave : handleEdit}
                        >
                            {editing ? <icons.Floppy /> : <icons.Pencil />}
                        </IconButton>
                    )}
                    {editing ? (
                        <IconButton size="small" disableRipple onClick={handleCancel}>
                            <icons.Close />
                        </IconButton>
                    ) : (
                        <IconButton
                            size="small"
                            disabled={deleteDisabled}
                            disableRipple
                            onClick={handleDelete}
                        >
                            <icons.Delete />
                        </IconButton>
                    )}
                </Loader>
                {/* empty error meessage to keep buttons aligned to the text field */}
                {error && <FormHelperText> </FormHelperText>}
            </Box>
        </Box>
    );
}

export default EditableEmailAddress;
